import { useEffect, useState } from "react"
import { Loader2, RefreshCw } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { formatDateTime } from "@/lib/format"
import { useDocType } from "@/pages/doc-type-management/presentation/hooks/use-doc-type"
import { useDocTypes } from "@/pages/doc-type-management/presentation/hooks/use-doc-types"
import { useKbUesConfig } from "@/pages/knowledge-bases/presentation/hooks/use-kb-ues-config"
import { useDocumentUes } from "../hooks/use-document-ues"
import { useReextractUes } from "../hooks/use-reextract-ues"
import { UesTable } from "./ues-table"

type UesTabProps = {
  docId: string
  kbId: string
}

export const UesTab = ({ docId, kbId }: UesTabProps) => {
  const { data, isLoading, isError } = useDocumentUes(docId)
  const { data: docTypes = [] } = useDocTypes()
  const {
    data: kbUesConfig,
    isPending: isKbUesConfigPending,
    isError: isKbUesConfigError,
  } = useKbUesConfig(kbId)
  const { mutate: reextract, isPending: isReextractPending } = useReextractUes()
  const [overrideDocTypeId, setOverrideDocTypeId] = useState("")
  const ues = data?.ues
  const uesStatus = data?.status
  const isRunning = uesStatus === "running"
  const { data: docType } = useDocType(ues?.docTypeId ?? "")

  useEffect(() => {
    setOverrideDocTypeId("")
  }, [docId])

  const isDefaultConfigUnavailable =
    overrideDocTypeId === "" && (isKbUesConfigPending || isKbUesConfigError)

  const defaultWouldNoOp =
    overrideDocTypeId === "" &&
    kbUesConfig !== undefined &&
    (!kbUesConfig.enabled ||
      (Array.isArray(kbUesConfig.docTypeIds) &&
        kbUesConfig.docTypeIds.length === 0))

  const handleReextract = () => {
    reextract({
      docId,
      docTypeId: overrideDocTypeId || undefined,
    })
  }

  return (
    <div className="space-y-5">
      {/* Override doc type + re-extract (right-aligned) */}
      <div className="flex items-end gap-3">
        <div className="flex flex-1 flex-col gap-1.5">
          <label htmlFor="ues-doc-type-override" className="text-sm font-medium">
            Override doc type
          </label>
          <select
            id="ues-doc-type-override"
            value={overrideDocTypeId}
            onChange={(event) => setOverrideDocTypeId(event.target.value)}
            disabled={isReextractPending}
            className={cn(
              "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs outline-none",
              "focus:ring-1 focus:ring-ring",
              "disabled:cursor-not-allowed disabled:opacity-50",
            )}
          >
            <option value="">Use knowledge base configuration</option>
            {docTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.displayName}
              </option>
            ))}
          </select>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0"
          onClick={handleReextract}
          disabled={isReextractPending || isRunning || isDefaultConfigUnavailable || isLoading}
        >
          {isReextractPending || isRunning ? (
            <Loader2 className="animate-spin" />
          ) : (
            <RefreshCw />
          )}
          Re-extract
        </Button>
      </div>

      {overrideDocTypeId === "" && isKbUesConfigPending && (
        <p className="text-sm text-muted-foreground">Loading UES config…</p>
      )}
      {overrideDocTypeId === "" && isKbUesConfigError && (
        <p className="text-sm text-destructive">Unable to load UES config.</p>
      )}
      {defaultWouldNoOp && (
        <p className="text-sm text-amber-700" role="alert">
          The current knowledge base UES configuration will produce no UES.
          Choose an override doc type to force extraction.
        </p>
      )}

      {isRunning && (
        <div className="flex items-center gap-2 py-2 text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Đang trích xuất UES…
        </div>
      )}
      {uesStatus === "failed" && (
        <p className="py-2 text-destructive" role="alert">
          Trích xuất UES thất bại. Hãy thử lại.
        </p>
      )}

      {isLoading ? (
        <div className="flex items-center gap-2 py-6 text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Loading UES…
        </div>
      ) : isError ? (
        <p className="py-4 text-destructive">Unable to load UES.</p>
      ) : ues ? (
        <div className="space-y-4">
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-medium">{ues.docTypeName}</h3>
              <div className="ml-auto flex items-center gap-2">
                <Badge variant="secondary">Schema v{ues.schemaVersion}</Badge>
                {data?.stale && (
                  <Badge variant="warning">
                    Schema updated — re-extract to refresh
                  </Badge>
                )}
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Extracted {formatDateTime(ues.extractedAt)}
            </p>
          </div>

          <UesTable fields={ues.fields} defs={docType?.uesFields} />
        </div>
      ) : (
        <p className="text-muted-foreground">No extraction yet</p>
      )}
    </div>
  )
}
